import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import PageLimit from "Components/common/PageLimit";
import Utils from "Utils/utils";
import PaginationComponent from "Components/common/PaginationComponent";
import { MP_EVENT } from "Static/constant";
import makeApi, { URLS } from "Utils/apiURL";
import CustomTooltip from "Components/common/CustomTooltip";
import ImportProgressbar from "Components/common/ImportProgressbar";
import ProcessingLoader from "Components/common/ProcessingLoader";
import Button from "Components/common/Button";
import { toasterState } from "Components/common/toaster";
import { exportProcessingState } from "Components/popup/ExportProcessing";
import bulkJobsNotificationStore from "Stores/bulkJobsNotification";
import { ReactComponent as ExportIcon } from "Assets/svg/exportCsv.svg";

const Exports = observer(() => {
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pages, setPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [showTooltip, setShowTooltip] = useState({});
  const [isJobInprocess, setIsJobInprocess] = useState(false);

  const statusField = {
    PROCESSING: {
      status: "In Progress",
      component: <ProcessingLoader />
    },
    TO_BE_PROCESSED: {
      status: "To be processed",
      color: "#9e9e9e"
    },
    PROCESSED: {
      status: "Completed",
      color: "#3953FB"
    },
    FAILED: {
      status: "Failed",
      color: "red"
    }
  };

  const successPercentage = (totalCount, successCount) => {
    if (totalCount === 0) {
      return 100;
    }
    const successPercent = (successCount / totalCount) * 100;
    return Math.round(successPercent);
  };

  const progressStatusField = {
    SUCCESS: {
      status: (totalCount, successCount) =>
        successPercentage(totalCount, successCount),
      color: "#00D274"
    }
  };

  const headerList = [
    { name: "Date", key: "createdDate" },
    { name: "Total ", key: "totalContacts" },
    { name: "Processed", key: "contactsProcessed" },
    { name: "Progress", key: "contactsProgress" },
    { name: "Status", key: "status" },
    { name: "Owner", key: "ownerName" },
    { name: "Export", key: "exportCsv" }
  ];

  const checkIfProcessing = data => {
    const isJobInProgress = data.some((item, ind) => {
      return item.status === "PROCESSING";
    });
    if (isJobInProgress) {
      setIsJobInprocess(true);
    } else {
      setIsJobInprocess(false);
    }
  };

  const getExportsData = async () => {
    try {
      const config = {
        url: URLS.exports,
        method: "POST",
        data: {
          limit,
          pageNo
        }
      };
      const res = await makeApi(config);

      const {
        jobs = [],
        currentPage: jobPageNo = 1,
        totalPages,
        totalJobs
      } = res?.data || {};
      if (res?.data) {
        if (totalJobs && jobs?.length) {
          setData(jobs);
          setPageNo(jobPageNo);
          setPages(totalPages);
          setTotalJobs(totalJobs);
          checkIfProcessing(jobs);
        } else if (res?.response?.status === 401) {
          Utils.showLoggedOutPopup();
        }
      } else {
        Utils.showApiFailureResponse(res);
      }
      setLoader(false);
    } catch (err) {
      console.error("Fetching exports data error", err);
    }
  };

  const paginate = pageNoObj => {
    setPageNo(pageNoObj?.selected + 1 || 1);
  };

  const handleActionByLimit = (limit = 10) => {
    setLimit(limit);
  };

  const handleExportResponse = downloadLink => {
    if (!downloadLink) {
      exportProcessingState.setShowPopup(false);
      toasterState.setToastMsg(
        "Something went wrong. Please try again",
        "failure"
      );
    } else {
      exportProcessingState.completeAnimation();
      setTimeout(() => {
        Utils.downloadExportedContact(downloadLink);
        exportProcessingState.setShowPopup(false);
        toasterState.setToastMsg("Export is successfully completed", "success");
      }, 1000);
    }
  };

  const downloadExportCsv = urls => {
    exportProcessingState.setShowPopup(true);

    urls.forEach(url => {
      const link = document.createElement("a");
      link.href = url;
      link.download = url.split("/").pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });

    handleExportResponse(urls[0]);
  };

  const wasInProgressRef = useRef(false);

  useEffect(() => {
    let jobStatusInterval;
    if (isJobInprocess) {
      wasInProgressRef.current = true;
      jobStatusInterval = setInterval(() => {
        getExportsData();
      }, 5000);
    } else if (wasInProgressRef.current) {
      wasInProgressRef.current = false;
      bulkJobsNotificationStore.fetchJobs();
      bulkJobsNotificationStore.triggerGuide("Your CSV export job is completed.");
    }
    return () => {
      if (jobStatusInterval) clearInterval(jobStatusInterval);
    };
  }, [isJobInprocess]);
  useEffect(() => {
    setLoader(true);
    getExportsData();
  }, [pageNo, limit]);

  useEffect(() => {
    Utils.mixpanelTrack(MP_EVENT.CSV_IMPORTS_VIEWED);
    return () => {
      setLoader(true);
      setData([]);
      setLimit(10);
      setPageNo(1);
      setPages(1);
    };
  }, []);

  return (
    <div className="csvImportsSection">
      <div className="tableList">
        <div className="tableHeader tableHeaderExports">
          {headerList?.map((item, index) => {
            const { name = "", key = "" } = item || {};
            return (
              <div className={`titleRow ${key}`} key={`csvImports${index}`}>
                {name}
              </div>
            );
          })}
        </div>
        {!loader ? (
          <>
            {(data?.length && (
              <div className="tableRecordHeightAlign">
                {data?.map((item, index) => {
                  const {
                    id,
                    createTs,
                    owner,
                    status = "",
                    totalContacts = "",
                    processedContacts,
                    urls = []
                  } = item || {};
                  return (
                    <div
                      className={`tableBody tableBodyExports ${id}`}
                      key={`csvTableBody${index}`}
                    >
                      <div className={`titleRow createdDate`}>
                        {Utils.getDateFromString(createTs || "--")}
                      </div>
                      <div className={`titleRow totalContacts`}>
                        {Utils.formatNumberToString(totalContacts) || "--"}
                      </div>
                      <div className={`titleRow contactsProcessed`}>
                        {Utils.formatNumberToString(processedContacts) || "--"}
                      </div>
                      <div className={`titleRow contactsProgress`}>
                        {["PROCESSING"].includes(status) ? (
                          <div className="progressbarWrapper">
                            <ImportProgressbar
                              total={totalContacts}
                              processed={processedContacts}
                            />
                          </div>
                        ) : ["PROCESSED", "TO_BE_PROCESSED", "FAILED"].includes(
                            status
                          ) ? (
                          <>
                            <div className="progressStatusWrapper">
                              <div className={`progressStatus`}>
                                <div
                                  className={`statusDot`}
                                  style={{
                                    backgroundColor: `${progressStatusField["SUCCESS"]?.color}`
                                  }}
                                ></div>
                                <div>
                                  {statusField[status]
                                    ? progressStatusField["SUCCESS"].status(
                                        totalContacts,
                                        processedContacts
                                      ) + "% Success"
                                    : "--"}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          ""
                        )}
                      </div>
                      <div className={`status`}>
                        {status === "PROCESSING" ? (
                          <div className="loader-wrapper">
                            {statusField[status]?.component}
                          </div>
                        ) : (
                          <div
                            className={`statusDot`}
                            style={{
                              backgroundColor: `${statusField[status]?.color}`
                            }}
                          ></div>
                        )}
                        <div>{statusField[status]?.status || "--"}</div>
                      </div>
                      <div className={`ownerName`}>
                        {(owner && (
                          <div
                            className={`contactOwner`}
                            onMouseEnter={() =>
                              setShowTooltip({ key: id, value: true })
                            }
                            onMouseLeave={() =>
                              setShowTooltip({ key: id, value: false })
                            }
                          >
                            <span className="circleOwner">
                              {Utils.getOwnerInitial(owner)}
                            </span>
                            {showTooltip?.key === id && showTooltip?.value && (
                              <CustomTooltip
                                text={`Added by ${Utils.getOwnerName(owner)}`}
                              />
                            )}
                          </div>
                        )) ||
                          "--"}
                      </div>
                      <div className={`titleRow exportCsv`}>
                        {urls !== null &&
                        Array.isArray(urls) &&
                        ["PROCESSED"].includes(status) ? (
                          <div className="downLoadCsv">
                            <ExportIcon
                              className="exportSvg"
                              onClick={() => downloadExportCsv(urls)}
                            />
                          </div>
                        ) : (
                          <>--</>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )) || (
              <div className="noDataFound">
                <i className="material-icons-outlined">list_alt</i>
                <span className="text">No data found</span>
              </div>
            )}
          </>
        ) : (
          <div className="adjustHt">
            {Array.from({ length: 8 }, (value, index) => (
              <div
                className="tableBodyLoader tableBodyLoaderExports"
                key={`tableBodyLoader${index + 1}`}
              >
                {headerList?.map((item, idx) => {
                  const { key = "" } = item || {};
                  return (
                    <div className={`${key}`} key={`${key}${index}${idx}`}>
                      <span className={`colLoader linear-background`} />
                    </div>
                  );
                })}
              </div>
            ))}{" "}
          </div>
        )}
      </div>
      <div className="inboxPaginationAndLimit">
        <PaginationComponent
          callback={paginate}
          loading={loader}
          totalPages={pages}
          currentPage={pageNo || 1}
        />
        <PageLimit
          limitPerPage={limit}
          text="Showing"
          cbk={handleActionByLimit}
          totalCount={totalJobs}
        />
      </div>
    </div>
  );
});

export default withRouter(Exports);
export { Exports };
